import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "../api";
import { useAuth } from "../auth";
import { useI18n, formatApiErrorDetail } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import { Sparkles } from "lucide-react";

export default function Register() {
  const { t } = useI18n();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", business_name: "", email: "", password: "", vat: "", address: "", phone: "",
  });
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!terms) { setErr(t.auth.termsRequired); return; }
    setLoading(true);
    try {
      const { data } = await client.post("/auth/register", form);
      loginWithToken(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || t.auth.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav variant="marketing" />
      <div className="flex-1 max-w-lg w-full mx-auto px-6 pt-10 pb-24" data-testid="register-page">
        <div className="text-center mb-7">
          <div className="flex justify-center mb-3"><Logo size="xl" linked={false} /></div>
          <h1 className="font-heading text-3xl tracking-tight">{t.auth.registerTitle}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{t.auth.registerSub}</p>
          <div className="mt-4 inline-flex chip chip-yellow">
            <Sparkles className="w-3 h-3" /> {t.landing.trial}
          </div>
        </div>
        <form onSubmit={submit} className="card-neon p-7 space-y-3" data-testid="register-form">
          <Field label={t.auth.fullName} value={form.full_name} onChange={set("full_name")} testId="reg-fullname" required />
          <Field label={t.auth.businessName} value={form.business_name} onChange={set("business_name")} testId="reg-business" required />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t.auth.vat} value={form.vat} onChange={set("vat")} testId="reg-vat" required />
            <Field label={t.auth.phone} value={form.phone} onChange={set("phone")} testId="reg-phone" />
          </div>
          <Field label={t.auth.address} value={form.address} onChange={set("address")} testId="reg-address" required />
          <Field label={t.auth.email} type="email" value={form.email} onChange={set("email")} testId="reg-email" required />
          <Field label={t.auth.password} type="password" value={form.password} onChange={set("password")} testId="reg-password" required minLength={6} />

          <label className="flex items-start gap-2 text-xs text-zinc-400 pt-2 cursor-pointer">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#00E676]" data-testid="reg-terms" />
            <span>
              {t.auth.acceptTerms.split("Termini")[0]}
              <Link to="/terms" className="text-neon-cyan hover:underline">Termini</Link>
              {t.auth.acceptTerms.includes("Termini") ? t.auth.acceptTerms.split("Termini")[1].split("Privacy")[0] : " "}
              <Link to="/privacy" className="text-neon-cyan hover:underline">Privacy</Link>
              {t.auth.acceptTerms.includes("Privacy") ? t.auth.acceptTerms.split("Privacy")[1] : ""}
            </span>
          </label>

          {err && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="register-error">{err}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit">
            {loading ? t.auth.processing : t.auth.submitRegister}
          </button>
          <div className="text-center text-xs text-zinc-500 pt-2">
            {t.auth.hasAccount} <Link to="/login" className="text-neon-cyan hover:underline" data-testid="register-to-login">{t.auth.loginCta}</Link>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, testId }) {
  return (
    <div>
      <label className="input-label">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} minLength={minLength} className="input-neon" data-testid={testId} />
    </div>
  );
}
